// ABOUTME: UI component for displaying different infinite scroll states at bottom of list
// ABOUTME: Handles loading, error, complete, and normal states with appropriate messaging
/* eslint-disable react/prop-types */
import { Box, Button, CircularProgress, Typography } from '@ds/coolshop';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { InfiniteScrollState } from '../hooks/useInfiniteScrollState';

interface InfiniteScrollBottomUIProps {
    state: InfiniteScrollState;
    errorMessage?: string | null;
    onRetry: () => void;
    mode: 'browse' | 'search';
}

export const InfiniteScrollBottomUI: React.FC<InfiniteScrollBottomUIProps> = memo(
    // eslint-disable-next-line react/prop-types
    ({ state, errorMessage, onRetry, mode }) => {
        const intl = useIntl();

        const getCompletedMessage = () =>
            mode === 'browse'
                ? intl.formatMessage({
                      id: 'sharepoint.all-items-loaded',
                      defaultMessage: 'Tutti gli elementi caricati',
                  })
                : intl.formatMessage({
                      id: 'sharepoint.all-results-loaded',
                      defaultMessage: 'Tutti i risultati caricati',
                  });

        const getLoadingMessage = () =>
            mode === 'browse'
                ? intl.formatMessage({
                      id: 'sharepoint.loading-more-items',
                      defaultMessage: 'Caricamento di altri elementi...',
                  })
                : intl.formatMessage({
                      id: 'sharepoint.loading-more-results',
                      defaultMessage: 'Caricamento di altri risultati...',
                  });

        switch (state) {
            case InfiniteScrollState.LOADING:
                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        padding="20px"
                        gap={2}
                    >
                        <CircularProgress size="medium" />
                        <Typography variant="body2" color="text.secondary">
                            {getLoadingMessage()}
                        </Typography>
                    </Box>
                );

            case InfiniteScrollState.ERROR:
                return (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        padding="20px"
                        gap="12px"
                    >
                        <Typography variant="body2" color="error">
                            {errorMessage ||
                                intl.formatMessage({
                                    id: 'sharepoint.failed-load-more',
                                    defaultMessage: 'Impossibile caricare altri elementi',
                                })}
                        </Typography>
                        <Button variant="contained" size="small" onClick={onRetry} color="primary">
                            {intl.formatMessage({
                                id: 'sharepoint.retry-load-more',
                                defaultMessage: 'Riprova',
                            })}
                        </Button>
                    </Box>
                );

            case InfiniteScrollState.COMPLETE:
                return (
                    <Box display="flex" justifyContent="center" alignItems="center" padding="20px">
                        <Typography variant="body2" color="text.secondary">
                            {getCompletedMessage()}
                        </Typography>
                    </Box>
                );

            case InfiniteScrollState.NORMAL:
            default:
                return null;
        }
    },
);

// PropTypes are handled by TypeScript interface
